/**
 * Authentication Hooks
 * React Query hooks for registration, password management, and OAuth
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  RegisterRequest,
  RegisterResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  VerifyResetTokenResponse,
  PasswordChangeHistory,
  TermsAcceptanceRequest,
  TermsAcceptanceResponse,
  LegalDocument,
  CheckEmailResponse,
} from '../types';

const API_BASE = '/api/v1/auth';

// Placeholder for actual API client
const apiClient = {
  get: async <T>(url: string, params?: Record<string, unknown>): Promise<{ data: T }> => {
    const response = await fetch(url + (params ? '?' + new URLSearchParams(params as Record<string, string>) : ''));
    return { data: await response.json() };
  },
  post: async <T>(url: string, data?: unknown): Promise<{ data: T }> => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data ? JSON.stringify(data) : undefined,
    });
    return { data: await response.json() };
  },
  delete: async <T>(url: string, config?: { data?: unknown }): Promise<{ data: T }> => {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: config?.data ? JSON.stringify(config.data) : undefined,
    });
    return { data: await response.json() };
  },
};

export function useRegister() {
  return useMutation<RegisterResponse, Error, RegisterRequest>({
    mutationFn: async (data) => {
      const response = await apiClient.post<RegisterResponse>(`${API_BASE}/register`, data);
      return response.data;
    },
  });
}

export function useVerifyEmail() {
  return useMutation<VerifyEmailResponse, Error, VerifyEmailRequest>({
    mutationFn: async (data) => {
      const response = await apiClient.post<VerifyEmailResponse>(`${API_BASE}/verify-email`, data);
      return response.data;
    },
  });
}

export function useResendVerification() {
  return useMutation<ResendVerificationResponse, Error, ResendVerificationRequest>({
    mutationFn: async (data) => {
      const response = await apiClient.post<ResendVerificationResponse>(`${API_BASE}/resend-verification`, data);
      return response.data;
    },
  });
}

export function useCheckEmail(email: string) {
  return useQuery<CheckEmailResponse>({
    queryKey: ['check-email', email],
    queryFn: async () => {
      const response = await apiClient.get<CheckEmailResponse>(`${API_BASE}/check-email`, { email });
      return response.data;
    },
    enabled: email.length > 5 && email.includes('@'),
  });
}

export function useForgotPassword() {
  return useMutation<void, Error, ForgotPasswordRequest>({
    mutationFn: async (data) => {
      await apiClient.post(`${API_BASE}/forgot-password`, data);
    },
  });
}

export function useResetPassword() {
  return useMutation<void, Error, ResetPasswordRequest>({
    mutationFn: async (data) => {
      await apiClient.post(`${API_BASE}/reset-password`, data);
    },
  });
}

export function useVerifyResetToken(token: string | null) {
  return useQuery<VerifyResetTokenResponse>({
    queryKey: ['verify-reset-token', token],
    queryFn: async () => {
      if (!token) throw new Error('No token provided');
      const response = await apiClient.get<VerifyResetTokenResponse>(`${API_BASE}/verify-reset-token`, { token });
      return response.data;
    },
    enabled: !!token,
    retry: false,
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ChangePasswordRequest>({
    mutationFn: async (data) => {
      await apiClient.post('/api/v1/users/change-password', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['password-history'] });
    },
  });
}

export function usePasswordHistory() {
  return useQuery<PasswordChangeHistory[]>({
    queryKey: ['password-history'],
    queryFn: async () => {
      const response = await apiClient.get<PasswordChangeHistory[]>('/api/v1/users/password-history');
      return response.data;
    },
  });
}

export function useAcceptTerms() {
  return useMutation<TermsAcceptanceResponse, Error, TermsAcceptanceRequest>({
    mutationFn: async (data) => {
      const response = await apiClient.post<TermsAcceptanceResponse>(`${API_BASE}/accept-terms`, data);
      return response.data;
    },
  });
}

export function useLegalDocument(type: 'terms' | 'privacy' | 'cookies') {
  return useQuery<LegalDocument>({
    queryKey: ['legal', type],
    queryFn: async () => {
      const response = await apiClient.get<LegalDocument>(`/api/v1/legal/${type}`);
      return response.data;
    },
  });
}
